import { useEffect, useState, useMemo, useRef } from 'react';
import { MapPin, Sparkles, Search, CheckCircle2, Zap, Star, TrendingUp } from 'lucide-react';
import type { AiMode } from '@/hooks/useSettings';
import { useLanguage } from '@/hooks/useLanguage';

export type LoadingStage =
  | 'validating'
  | 'searching'
  | 'topup'
  | 'ranking'
  | 'complete';

export type SearchSubStage = 'vibes' | 'custom' | 'merging';
export type RankingSubStage = 'sending' | 'analysing' | 'filtering';

interface LoadingScreenProps {
  stage?: LoadingStage;
  locationName?: string;
  placesFound?: number;
  placesEliminated?: number;
  showTopupStage?: boolean;
  firstPassAnalyzed?: number;
  firstPassEliminated?: number;
  rankingSubStage?: RankingSubStage;
  searchSubStage?: SearchSubStage;
  searchProgress?: { completed: number; total: number };
  aiMode?: AiMode;
}

const AI_MODE_STYLE: Record<AiMode, { color: string; icon: typeof Sparkles }> = {
  off: { color: 'var(--text-secondary)', icon: Star },
  low: { color: 'var(--text-secondary)', icon: Zap },
  medium: { color: 'var(--accent-gold)', icon: Sparkles },
  high: { color: 'var(--accent-teal)', icon: TrendingUp },
};

const STAGE_DURATIONS: Record<LoadingStage, number> = {
  validating: 3000,
  searching: 8000,
  topup: 8000,
  ranking: 20000,
  complete: 1000,
};


function StepProgressBar({
  isActive,
  isComplete,
  stageId,
  color,
  subStage,
  searchProgress,
}: {
  isActive: boolean;
  isComplete: boolean;
  stageId: LoadingStage;
  color: string;
  subStage?: RankingSubStage;
  searchProgress?: { completed: number; total: number };
}) {
  const isRankingStep = stageId === 'ranking';
  const isSearchStep = stageId === 'searching';
  const effectiveSubStage = isRankingStep ? (subStage ?? 'sending') : undefined;

  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const [displaySubStage, setDisplaySubStage] = useState<RankingSubStage>('sending');

  useEffect(() => {
    if (!isActive || !isRankingStep) return;

    if (effectiveSubStage === 'analysing' || effectiveSubStage === 'filtering') {
      setDisplaySubStage(effectiveSubStage);
    } else if (effectiveSubStage === 'sending') {
      setDisplaySubStage('sending');
    }
  }, [effectiveSubStage, isActive, isRankingStep]);

  useEffect(() => {
    if (isRankingStep && isActive && displaySubStage === 'sending' && progress >= 70) {
      setDisplaySubStage('analysing');
    }
  }, [progress, isRankingStep, isActive, displaySubStage]);

  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

    if (isComplete) {
      setProgress(100);
      return;
    }

    if (!isActive) {
      setProgress(0);
      startTimeRef.current = null;
      return;
    }

    if (isSearchStep && searchProgress && searchProgress.total > 0) {
      const pct = (searchProgress.completed / searchProgress.total) * 92;
      setProgress(pct);
      return;
    }

    startTimeRef.current = null;
    const duration = STAGE_DURATIONS[stageId];

    const startId = requestAnimationFrame((firstNow) => {
      startTimeRef.current = firstNow;
      const animate = (now: number) => {
        const elapsed = now - (startTimeRef.current ?? now);
        const t = elapsed / duration;
        const pct = 97 * (1 - 1 / (1 + t * 1.8));
        setProgress(Math.min(pct, 97));
        rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    });
    rafRef.current = startId;

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, isComplete, stageId, isSearchStep, searchProgress]);

  if (!isActive && !isComplete) return null;

  const isFiltering = isRankingStep && isActive && displaySubStage === 'filtering';
  const isSending = isRankingStep && isActive && displaySubStage === 'sending';
  const isAnalysing = isRankingStep && isActive && displaySubStage === 'analysing';

  const subLabel = isSending
    ? 'Sending to AI...'
    : isAnalysing
      ? 'Processing response...'
      : isFiltering
        ? 'Filtering results...'
        : null;

  return (
    <div className="mt-2.5 space-y-1">
      {subLabel && (
        <p className="text-xs text-[var(--text-secondary)] animate-fade-in">
          {subLabel}
        </p>
      )}
      <div className="h-1 w-full rounded-full overflow-hidden bg-[var(--bg-elevated)]">
        <div
          className="h-full rounded-full"
          style={{
            width: `${isComplete ? 100 : progress}%`,
            background: isComplete
              ? 'var(--accent-teal)'
              : isFiltering
                ? 'linear-gradient(90deg, var(--accent-gold) 0%, var(--accent-teal) 100%)'
                : `linear-gradient(90deg, ${color} 0%, var(--accent-gold) 100%)`,
            transition: isComplete ? 'width 0.3s ease-out' : 'none',
          }}
        />
      </div>
    </div>
  );
}

export function LoadingScreen({
  stage = 'validating',
  locationName,
  placesFound,
  showTopupStage = false,
  firstPassAnalyzed,
  firstPassEliminated,
  rankingSubStage = 'sending',
  searchSubStage,
  searchProgress,
  aiMode = 'medium'
}: LoadingScreenProps) {
  const [showPlacesCount, setShowPlacesCount] = useState(false);
  const { t } = useLanguage();
  const modeStyle = AI_MODE_STYLE[aiMode];
  const modeLabel = `${t.settings.aiMode}: ${t.settings.aiLevels[aiMode]}`;

  useEffect(() => {
    if (stage === 'ranking' && placesFound && placesFound > 0) {
      const timer = setTimeout(() => setShowPlacesCount(true), 300);
      return () => clearTimeout(timer);
    }
    setShowPlacesCount(false);
  }, [stage, placesFound]);

  const stages = useMemo(() => {
    const isAiOff = aiMode === 'off';
    const isLow = aiMode === 'low';

    const stageList: Array<{
      id: LoadingStage;
      label: string;
      icon: typeof MapPin;
      subLabel?: string;
    }> = [
      {
        id: 'validating',
        label: locationName ? t.loadingStages.checkingLocationNamed.replace('{name}', locationName) : t.loadingStages.checkingLocation,
        icon: MapPin
      },
      {
        id: 'searching',
        label: t.loadingStages.discoveringPlaces,
        icon: Search,
        subLabel: searchProgress && searchProgress.total > 0
          ? t.loadingStages.searchesComplete.replace('{completed}', String(searchProgress.completed)).replace('{total}', String(searchProgress.total))
          : searchSubStage === 'custom'
            ? t.loadingStages.searchingCustomVibe
            : searchSubStage === 'merging'
              ? t.loadingStages.mergingResults
              : undefined
      },
    ];

    // Topup now runs before ranking — show it earlier in the flow
    if (showTopupStage) {
      stageList.push({
        id: 'topup',
        label: aiMode === 'high' ? t.loadingStages.deepSearching : t.loadingStages.findingMore,
        icon: Search
      });
    }

    stageList.push({
      id: 'ranking',
      label: isAiOff
        ? t.loadingStages.sortingByRating
        : isLow
          ? t.loadingStages.aiMatchingVibes
          : t.loadingStages.aiAnalysing,
      icon: isAiOff ? Star : Sparkles,
      subLabel: !isAiOff && firstPassAnalyzed && firstPassEliminated !== undefined
        ? t.loadingStages.analysedEliminated.replace('{analysed}', String(firstPassAnalyzed)).replace('{eliminated}', String(firstPassEliminated))
        : undefined
    });

    return stageList;
  }, [locationName, showTopupStage, firstPassAnalyzed, firstPassEliminated, aiMode, searchProgress, searchSubStage, t]);

  const stageOrder: LoadingStage[] = showTopupStage
    ? ['validating', 'searching', 'topup', 'ranking', 'complete']
    : ['validating', 'searching', 'ranking', 'complete'];

  const currentStageIndex = stageOrder.indexOf(stage);

  const getStageStatus = (stageId: LoadingStage) => {
    const stageIndex = stageOrder.indexOf(stageId);
    if (stageIndex < currentStageIndex) return 'complete';
    if (stageIndex === currentStageIndex) return 'active';
    return 'pending';
  };

  const ModeIcon = modeStyle.icon;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-hero opacity-50" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-96 h-96 rounded-full opacity-20"
          style={{
            background: `radial-gradient(circle, ${modeStyle.color} 0%, transparent 70%)`,
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            animation: 'breathe 3s ease-in-out infinite'
          }}
        />
        <div
          className="absolute w-64 h-64 rounded-full opacity-10"
          style={{
            background: 'radial-gradient(circle, var(--accent-gold) 0%, transparent 70%)',
            bottom: '20%',
            right: '10%',
            animation: 'breathe 4s ease-in-out infinite 1s'
          }}
        />
      </div>

      <div className="relative z-10 text-center space-y-10 max-w-md mx-auto">
        <div className="space-y-3">
          <p className="text-4xl font-semibold tracking-tight text-[var(--text-primary)] text-center">
            Trip<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boom</span>
          </p>
          {locationName && (
            <p className="text-sm text-[var(--accent-teal)] animate-fade-in">
              From: {locationName}
            </p>
          )}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full animate-fade-in"
            style={{
              background: `${modeStyle.color}12`,
              border: `1px solid ${modeStyle.color}30`,
            }}
          >
            <ModeIcon className="w-3.5 h-3.5" style={{ color: modeStyle.color }} />
            <span className="text-xs font-medium" style={{ color: modeStyle.color }}>
              {modeLabel}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {stages.map((s, index) => {
            const status = getStageStatus(s.id);
            const Icon = s.icon;
            const isActive = status === 'active';
            const isComplete = status === 'complete';
            const stepSubStage = s.id === 'ranking' && isActive
              ? rankingSubStage
              : undefined;

            return (
              <div
                key={s.id}
                className={`
                  flex flex-col px-5 py-4 rounded-xl transition-all duration-500
                  ${isActive
                    ? 'bg-[var(--bg-card)] border border-[var(--accent-teal)] shadow-[0_0_0_1px_var(--accent-teal)] scale-[1.02]'
                    : isComplete
                      ? 'bg-[var(--bg-card)]/50 border border-[var(--border)]'
                      : 'bg-transparent border border-transparent opacity-40'
                  }
                `}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center gap-4">
                  <div className={`
                    w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300
                    ${isActive
                      ? 'bg-[var(--accent-teal)]/20 text-[var(--accent-teal)]'
                      : isComplete
                        ? 'bg-[var(--accent-teal)]/10 text-[var(--accent-teal)]'
                        : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'
                    }
                  `}>
                    {isComplete ? (
                      <CheckCircle2 className="w-4.5 h-4.5" />
                    ) : (
                      <Icon className="w-4.5 h-4.5" />
                    )}
                  </div>

                  <div className="flex-1 text-left min-w-0">
                    <p className={`
                      text-sm font-medium transition-colors duration-300
                      ${isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}
                    `}>
                      {s.label}
                    </p>

                    {s.id === 'searching' && isActive && s.subLabel && (
                      <p className="text-xs text-[var(--accent-gold)] mt-0.5 animate-fade-in">
                        {s.subLabel}
                      </p>
                    )}
                    {s.id === 'ranking' && isActive && showPlacesCount && placesFound && !firstPassAnalyzed && (
                      <p className="text-xs text-[var(--accent-gold)] mt-0.5 animate-fade-in">
                        {aiMode === 'off' ? `Sorting ${placesFound} places` : `Analysing ${placesFound} places`}
                      </p>
                    )}
                    {s.id === 'ranking' && isComplete && s.subLabel && (
                      <p className="text-xs text-[var(--accent-gold)] mt-0.5 animate-fade-in">
                        {s.subLabel}
                      </p>
                    )}
                  </div>

                  {isComplete && (
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-[var(--accent-teal)]" />
                    </div>
                  )}
                </div>

                <StepProgressBar
                  isActive={isActive}
                  isComplete={isComplete}
                  stageId={s.id}
                  color={modeStyle.color}
                  subStage={stepSubStage}
                  searchProgress={s.id === 'searching' ? searchProgress : undefined}
                />
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes breathe {
          0%, 100% { transform: translateX(-50%) scale(1); opacity: 0.15; }
          50% { transform: translateX(-50%) scale(1.1); opacity: 0.25; }
        }
      `}</style>
    </div>
  );
}
